"use client";

import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";
import noOrder from "@/assets/images/account/no-order.webp";
import { useConfirm } from "@/components/global/confirm-context";
import { PopLoading } from "@/components/pop-ui/pop-loading";
import { toast } from "@/components/pop-ui/pop-toast";
import { PullToRefreshWrapper } from "@/components/ui/pull-to-refresh";
import { useIsMobile } from "@/hooks/use-mobile";
import UserOrderItem from "@/modules/order/components/user-order-item";
import { useOnlineOrders } from "@/modules/order/hooks";
import { OrderStatusEnum } from "@/modules/order/schema";
import { validateOldOrderNumber } from "@/modules/order/utils";
import { useORPC } from "@/providers/orpc-provider";
import { useTracking } from "@/track";
import Empty from "./components/empty";
import { ModulePagination } from "./components/module-pagination";
import { ModuleTitle } from "./components/module-title";
const pageSize = 20;
interface OrdersModuleProps {
  onConfirmDialogStateChange?: (isOpen: boolean) => void;
}
export function OrdersModule({
  onConfirmDialogStateChange
}: OrdersModuleProps) {
  const {
    t
  } = useTranslation();
  const orpc = useORPC();
  const confirm = useConfirm();
  const isMobile = useIsMobile();

  // 分页相关状态
  const [page, setPage] = React.useState(1);

  // 节流相关状态
  const lastOrderExpiredTimeRef = React.useRef<number>(0);
  const orderExpiredTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const THROTTLE_DELAY = 5 * 1000; // 5秒

  const {
    track,
    TrackingEventType
  } = useTracking();

  // 处理分页变化
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // 重置滚动条到顶部
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // 统一的取消订单 mutation
  const cancelOrderMutation = useMutation({
    mutationFn: (params: {
      orderNumber: string;
      reason?: string;
    }) => orpc.ec.order.closeOrder.call(params),
    onSuccess: () => {
      toast.success(t("order.toast.cancelSuccess", "订单已成功取消"));
      refetch();
    },
    onError: (error: Error) => {
      console.error("Failed to cancel order:", error);
      refetch();
    }
  });
  const handleCancelOrder = async (orderNumber: string) => {
    track("PopMartGlobalWebCancelOrderClick", {
      product_type: "mall",
      order_id: orderNumber
    }, TrackingEventType.CLICK);
    onConfirmDialogStateChange?.(true);
    try {
      const _ok = await confirm({
        title: t("order.confirm.cancelTitle"),
        description: t("order.confirm.cancelDescription"),
        closeOnError: true,
        onConfirmAction: async () => {
          await cancelOrderMutation.mutateAsync({
            orderNumber,
            reason: "用户取消"
          });
        }
      });
    } finally {
      // Notify parent that confirm dialog is closed
      onConfirmDialogStateChange?.(false);
    }
  };
  const {
    data: userOrders,
    isLoading,
    refetch
  } = useOnlineOrders({
    page,
    pageSize
  });

  // 下拉刷新处理函数
  const handleRefresh = async () => {
    await refetch();
  };

  // 节流的订单过期处理函数
  const handleOrderExpired = React.useCallback((userOrder: any) => {
    const now = Date.now();
    if (now - lastOrderExpiredTimeRef.current < THROTTLE_DELAY) {
      return; // 在节流期间，忽略调用
    }
    if (userOrder.status === OrderStatusEnum.WAIT_PAY) {
      lastOrderExpiredTimeRef.current = now;

      // 清理之前的定时器
      if (orderExpiredTimerRef.current) {
        clearTimeout(orderExpiredTimerRef.current);
      }

      // 延迟2秒后刷新，给服务端留出修改订单状态的时间
      orderExpiredTimerRef.current = setTimeout(() => {
        refetch();
        orderExpiredTimerRef.current = null;
      }, 2000);
    }
  }, [refetch]);

  // 组件卸载时清理定时器
  React.useEffect(() => {
    return () => {
      if (orderExpiredTimerRef.current) {
        clearTimeout(orderExpiredTimerRef.current);
      }
    };
  }, []);
  const contentDom = () => {
    // 加载中
    if (isLoading) {
      return <div className="min-h-[500px]">
					<ModuleTitle title={t("account.orders.title", "我的訂單")} />
					<div className="flex justify-center items-center py-10">
						<PopLoading />
					</div>
				</div>;
    }

    // 无订单
    if (!userOrders?.items || userOrders.items?.length === 0) {
      return <div>
					<ModuleTitle title={t("account.orders.title", "我的訂單")} />
					<Empty src={noOrder} title={t("account.orders.emptyText", "您暫無訂單")} />
				</div>;
    }
    return <div>
				<ModuleTitle title={t("account.orders.title", "我的訂單")} />
				<div className="flex flex-col md:gap-4 gap-3">
					{userOrders.items?.map((userOrder_0: any) => <UserOrderItem key={userOrder_0.id} order={userOrder_0} onCancel={handleCancelOrder} onOrderExpired={() => handleOrderExpired(userOrder_0)} isOldOrder={validateOldOrderNumber(userOrder_0.orderNumber)} />)}
				</div>
				<ModulePagination currentPage={page} totalPages={Math.ceil((userOrders?.total || 0) / pageSize)} onPageChange={handlePageChange} />
			</div>;
  };

  // 在移动端使用下拉刷新
  return isMobile ? <PullToRefreshWrapper onRefresh={handleRefresh}>
			{contentDom()}
		</PullToRefreshWrapper> : contentDom();
}